/**
 * Compact Bold/Italic/Underline row for the mini ProseMirror editors inside HeaderFooterBtn.tsx's
 * and FootnoteEndnoteBtn.tsx's popovers. Those editors previously had no formatting controls at
 * all — variable insertion only — explicitly flagged as "a follow-up" in both components' own
 * header comments. Reuses the exact `runFormat`-mark pattern `CdmToolbar.tsx` uses for the main
 * editor (schema.ts's "attrs ARE the model" design — every button reads/writes the whole
 * `RunFormat` value object via `setMark`, not a stock Tiptap toggle command, since this schema
 * doesn't define one). Deliberately narrow: bold/italic/underline only, not the main toolbar's
 * full set (font/size/color/alignment/lists/etc.) — these mini editors hold a line or two of
 * header/footer/footnote text, not a full document, so paragraph-level and list controls have no
 * real use case here yet.
 */
import type { useEditor } from '@tiptap/react'
import { useEditorState } from '@tiptap/react'
import { TBtn } from './ToolbarControls'
import { UnderlineStyle, defaultRunFormat, type RunFormat } from '../../cdm/types'

type CdmEditor = ReturnType<typeof useEditor>

export function MiniFormatToolbar({ editor }: { editor: CdmEditor }) {
  const runFormat = useEditorState({
    editor,
    selector: e => (e.editor?.getAttributes('runFormat').format as RunFormat | undefined) ?? defaultRunFormat(),
  }) ?? defaultRunFormat()

  if (!editor) return null

  function setRunFormat(patch: Partial<RunFormat>) {
    editor!.chain().focus().setMark('runFormat', { format: { ...runFormat, ...patch } }).run()
  }

  return (
    <div style={{ display: 'flex', gap: 2 }}>
      <TBtn active={runFormat.bold} onClick={() => setRunFormat({ bold: !runFormat.bold })} title="Bold"><b>B</b></TBtn>
      <TBtn active={runFormat.italic} onClick={() => setRunFormat({ italic: !runFormat.italic })} title="Italic"><em>I</em></TBtn>
      <TBtn
        active={runFormat.underline !== UnderlineStyle.None}
        onClick={() => setRunFormat({ underline: runFormat.underline === UnderlineStyle.None ? UnderlineStyle.Single : UnderlineStyle.None })}
        title="Underline"
      >
        <u>U</u>
      </TBtn>
    </div>
  )
}
